// Web micro-site control API client — wired to the real backend (Sprint 12, Agent W).
//
// Path prefix: `/api/v1/admin/web` on top of the `http` axios instance.
// All endpoints return the `ActionResponse<T>` envelope; we use `unwrap` /
// `unwrapList` from `./http`.
//
// BE-aligned shapes (see `src/types/api.ts`):
//   • WebRedirectDto              — redirect rule with rowVersion
//   • CreateWebRedirectRequest    — POST body
//   • UpdateWebRedirectRequest    — PUT body (requires rowVersion)
//   • OgPreviewDto                — server-rendered OG / Twitter meta preview

import {
  http,
  unwrap,
  unwrapList,
  type ActionResponse,
  type PaginationInfo,
} from './http';
import type {
  CreateRedirectInput,
  OgPreview,
  OgPreviewInput,
  RedirectListQuery,
  RedirectRule,
  UpdateRedirectInput,
} from '@features/web/types';

// ---- Raw BE DTOs (mirror src/types/api.ts) ---------------------------------

interface WebRedirectBeDto {
  id: string;
  fromPath: string | null;
  toPath: string | null;
  statusCode: number;
  isActive: boolean;
  hitCount: number;
  lastHitAt: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
  rowVersion: string | null;
}

interface OgPreviewBeDto {
  title: string | null;
  description: string | null;
  image: string | null;
  canonical: string | null;
  ogTitle: string | null;
  ogDescription: string | null;
  ogImage: string | null;
  ogType: string | null;
  ogUrl: string | null;
  twitterCard: string | null;
  twitterTitle: string | null;
  twitterDescription: string | null;
  twitterImage: string | null;
}

interface CreateWebRedirectBeRequest {
  fromPath: string;
  toPath: string;
  statusCode: number;
  isActive: boolean;
  notes?: string | null;
}

interface UpdateWebRedirectBeRequest {
  fromPath: string;
  toPath: string;
  statusCode: number;
  isActive: boolean;
  notes?: string | null;
  rowVersion: string | null;
}

// ---- Mappers ---------------------------------------------------------------

function mapRedirect(d: WebRedirectBeDto): RedirectRule {
  return {
    id: d.id,
    fromPath: d.fromPath ?? '',
    toPath: d.toPath ?? '',
    statusCode: d.statusCode,
    isActive: d.isActive,
    hitCount: d.hitCount,
    lastHitAt: d.lastHitAt,
    notes: d.notes,
    createdAt: d.createdAt,
    updatedAt: d.updatedAt,
    rowVersion: d.rowVersion,
  };
}

function mapOgPreview(d: OgPreviewBeDto): OgPreview {
  return {
    title: d.title ?? '',
    description: d.description ?? '',
    image: d.image ?? null,
    canonical: d.canonical ?? '',
    ogTitle: d.ogTitle ?? '',
    ogDescription: d.ogDescription ?? '',
    ogImage: d.ogImage ?? null,
    ogType: d.ogType ?? 'website',
    ogUrl: d.ogUrl ?? '',
    twitterCard: d.twitterCard ?? 'summary',
    twitterTitle: d.twitterTitle ?? '',
    twitterDescription: d.twitterDescription ?? '',
    twitterImage: d.twitterImage ?? null,
  };
}

function toCreateRequest(input: CreateRedirectInput): CreateWebRedirectBeRequest {
  return {
    fromPath: input.fromPath,
    toPath: input.toPath,
    statusCode: input.statusCode,
    isActive: input.isActive,
    notes: input.notes ?? null,
  };
}

function toUpdateRequest(
  input: UpdateRedirectInput,
  rowVersion: string | null,
): UpdateWebRedirectBeRequest {
  return {
    fromPath: input.fromPath,
    toPath: input.toPath,
    statusCode: input.statusCode,
    isActive: input.isActive,
    notes: input.notes ?? null,
    rowVersion,
  };
}

// ---- Public API ------------------------------------------------------------

export interface PaginatedResult<T> {
  items: T[];
  pagination: PaginationInfo | null;
  total: number;
}

const BASE = '/api/v1/admin/web';

export const webApi = {
  redirects: {
    async list(query: RedirectListQuery = {}): Promise<PaginatedResult<RedirectRule>> {
      const p = new URLSearchParams();
      if (query.search) p.set('search', query.search);
      if (query.isActive !== undefined) p.set('isActive', String(query.isActive));
      if (query.statusCode !== undefined)
        p.set('statusCode', String(query.statusCode));
      if (query.page) p.set('page', String(query.page));
      if (query.pageSize) p.set('pageSize', String(query.pageSize));
      if (query.sortBy) p.set('sortBy', query.sortBy);
      if (query.descending !== undefined)
        p.set('descending', String(query.descending));

      const qs = p.toString();
      const res = await http.get<ActionResponse<WebRedirectBeDto>>(
        qs ? `${BASE}/redirects?${qs}` : `${BASE}/redirects`,
      );
      const { items, pagination } = unwrapList(res);
      return {
        items: items.map(mapRedirect),
        pagination,
        total: pagination?.totalCount ?? items.length,
      };
    },

    async get(id: string): Promise<RedirectRule> {
      const res = await http.get<ActionResponse<WebRedirectBeDto>>(
        `${BASE}/redirects/${id}`,
      );
      return mapRedirect(unwrap(res));
    },

    /** Create returns the new Guid; re-fetch for the full DTO + rowVersion. */
    async create(input: CreateRedirectInput): Promise<RedirectRule> {
      const res = await http.post<ActionResponse<string>>(
        `${BASE}/redirects`,
        toCreateRequest(input),
      );
      const id = unwrap(res);
      return this.get(id);
    },

    /** PUT returns `data: true`. We re-fetch for the bumped rowVersion. */
    async update(
      id: string,
      input: UpdateRedirectInput,
      rowVersion: string | null,
    ): Promise<RedirectRule> {
      const res = await http.put<ActionResponse<boolean>>(
        `${BASE}/redirects/${id}`,
        toUpdateRequest(input, rowVersion),
      );
      unwrap(res);
      return this.get(id);
    },

    async remove(id: string): Promise<void> {
      const res = await http.delete<ActionResponse<boolean>>(
        `${BASE}/redirects/${id}`,
      );
      unwrap(res);
    },

    /** Flip isActive without a full PUT (no rowVersion needed per BE). */
    async toggleActive(id: string): Promise<void> {
      const res = await http.post<ActionResponse<boolean>>(
        `${BASE}/redirects/${id}/toggle-active`,
        {},
      );
      unwrap(res);
    },
  },

  /**
   * ISSUE-42 — Server-rendered OG / Twitter meta preview for a given content
   * slug + type. Audited as `WebOgPreviewBuilt` server-side.
   */
  ogPreview: {
    async build(input: OgPreviewInput): Promise<OgPreview> {
      const res = await http.post<ActionResponse<OgPreviewBeDto>>(
        `${BASE}/og-preview`,
        // BE enum is serialised as integer:
        // 0=Article, 1=Page, 3=Sss
        { type: input.type, slug: input.slug },
      );
      return mapOgPreview(unwrap(res));
    },
  },
};

export type {
  RedirectRule,
  CreateRedirectInput,
  UpdateRedirectInput,
  RedirectListQuery,
} from '@features/web/types';
